import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDownRight, Award, GraduationCap, Sparkles, Users } from "lucide-react";
import { PROFILE } from "../data/cv";
import { lineMask, stagger } from "../lib/anim";
import { scrollToId, openAIChat } from "./Nav";

const MaskedLine = ({ children, i = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span className="block will-change-transform" variants={lineMask} custom={i} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 16 });
  const sy = useSpring(my, { stiffness: 60, damping: 16 });
  const portraitRotateY = useTransform(sx, [-0.5, 0.5], ["9deg", "-9deg"]);
  const portraitRotateX = useTransform(sy, [-0.5, 0.5], ["-7deg", "7deg"]);
  const portraitTranslate = useTransform([sx, sy], ([a, b]) => `${a * 22}px ${b * 18}px`);
  const glowTranslate = useTransform([sx, sy], ([a, b]) => `${a * -46}px ${b * -34}px`);

  const onMouseMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMouseMove} className="relative min-h-screen flex items-center overflow-hidden">
      {/* backdrop */}
      <div className="absolute inset-0 grid-bg" />
      <motion.div style={{ translate: glowTranslate }} className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full bg-neon/15 orb" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-vio/15 orb" style={{ animationDelay: "-6s" }} />
      <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-fuchsia-600/10 orb" style={{ animationDelay: "-3s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-28 pb-16 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Copy */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
          <motion.p variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
            className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" />
            Pathology × Applied AI × Health-tech
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-[clamp(2.6rem,7vw,5.6rem)]">
            <MaskedLine i={0}>Dr. Rahul</MaskedLine>
            <MaskedLine i={1}><span className="text-grad">Kanungo</span></MaskedLine>
            <MaskedLine i={2}>
              <span className="text-[clamp(1.05rem,2.4vw,1.9rem)] font-semibold text-slate-300 tracking-normal leading-snug">
                Precision diagnostics. <span className="shimmer">Applied AI. Healthcare leadership.</span>
              </span>
            </MaskedLine>
          </h1>

          <motion.p variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.55 } } }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">
            {PROFILE.tagline}
          </motion.p>

          <motion.div variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.68 } } }}
            className="mt-4 flex flex-wrap gap-2.5">
            {[
              { icon: GraduationCap, text: "MD Pathology — Gold Medal, 2025" },
              { icon: Award, text: "MBA Healthcare & Hospital Mgmt" },
              { icon: Users, text: "Meta-verified · 17K+ organic audience" },
            ].map(({ icon: Icon, text }) => (
              <span key={text} className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 border border-white/10 rounded-full px-3.5 py-1.5 bg-white/[0.03]">
                <Icon size={13} className="text-neon" /> {text}
              </span>
            ))}
          </motion.div>

          <motion.div variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.8 } } }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-start-conversation-button"
              onClick={() => scrollToId("#engage")}
              className="group relative flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-ink bg-gradient-to-r from-neon via-indigo-400 to-vio hover:shadow-glow-cyan transition-shadow duration-300"
            >
              Start a conversation
              <ArrowDownRight size={17} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-ask-ai-button"
              onClick={openAIChat}
              className="flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-slate-200 border border-white/12 bg-white/[0.04] hover:border-neon/40 hover:text-white transition-colors duration-300"
            >
              <Sparkles size={16} className="text-neon" />
              Ask my AI twin
            </button>
          </motion.div>

          <motion.p variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 1, delay: 1 } } }}
            className="mt-8 flex items-center gap-2.5 text-xs font-mono text-slate-500">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            {PROFILE.availability}
          </motion.p>
        </motion.div>

        {/* Portrait */}
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end [perspective:1200px]">
          <motion.div
            style={reduced ? {} : { rotateY: portraitRotateY, rotateX: portraitRotateX, translate: portraitTranslate }}
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
            className="relative"
          >
            <div className="absolute -inset-8 bg-gradient-to-tr from-neon/25 via-indigo-500/15 to-vio/25 blur-3xl rounded-full" />
            <div className="relative clip-notch scanline p-[3px] bg-gradient-to-br from-neon/70 via-indigo-500/40 to-vio/70" data-testid="hero-portrait-frame">
              <div className="clip-notch bg-panel overflow-hidden relative">
                <img
                  src="/images/headshot.png"
                  alt="Dr. Rahul Kanungo"
                  className="w-[280px] sm:w-[330px] lg:w-[360px] h-auto object-cover"
                  style={{ imageRendering: "auto" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-neon/10 pointer-events-none" />
              </div>
            </div>

            {/* corner brackets */}
            <span className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-neon/70" />
            <span className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-vio/70" />

            {/* floating chips */}
            <div className="absolute -left-8 sm:-left-16 top-8 glass rounded-xl px-4 py-3 animate-floaty" style={{ animationDelay: "-1.5s" }}>
              <p className="font-display font-bold text-grad text-xl leading-none">4</p>
              <p className="text-[11px] text-slate-400 mt-1">health-tech products live</p>
            </div>
            <div className="absolute -right-4 sm:-right-10 bottom-10 glass rounded-xl px-4 py-3 animate-floaty">
              <p className="font-display font-bold text-grad text-xl leading-none">6</p>
              <p className="text-[11px] text-slate-400 mt-1">indexed publications</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-slate-600 to-transparent" />
      </div>
    </section>
  );
}